<template>
  <article
    class="lpu-doctor-card d-flex flex-column bg-gray-0 position-relative"
    :class="isMobile ? 'rounded-15 pa-4 ga-4' : 'rounded-24 pa-6'"
    data-qa="lpu_doctors_list_card"
  >
    <DoctorCardActions
      v-if="isMobile"
      class="lpu-doctor-card__actions"
      :doctor-id="doctor.id"
      :doctor-page-url="doctorPageUrl"
      :size="40"
      rounded-class="rounded-15"
    />

    <div
      class="d-flex"
      :class="isMobile ? 'flex-column ga-4' : 'flex-row ga-6 align-start'"
    >
      <DoctorCardHeader
        :doctor="doctor"
        :doctor-page-url="doctorPageUrl"
      />

      <div
        class="d-flex flex-column min-w-0 flex-grow-1"
        :class="isMobile ? 'ga-4' : 'ga-3'"
      >
        <DoctorCardIdentity
          :doctor="doctor"
          :doctor-page-url="doctorPageUrl"
        />

        <DoctorDocumentsPlate
          :doctor="doctor"
          :documents-url="`${doctorPageUrl}#documents`"
        />

        <div
          v-if="isStaff && !isMobile && doctor.degree_education?.title"
          class="adminka-box d-inline-flex align-self-start pa-1 my-1"
        >
          {{ doctor.degree_education.title }}
        </div>

        <template v-if="isMobile">
          <div
            v-if="positiveRate"
            class="lpu-doctor-card__divider"
          />
          <DoctorCardReview
            v-if="positiveRate"
            with-icon
            :href="`${doctorPageUrl}#otzivi`"
            :text="positiveRate"
          />
          <div
            v-if="showPriceBlock || clinicLoyaltyProgram"
            class="lpu-doctor-card__divider"
          />
        </template>
        <DoctorCardOffer
          :value="placeType"
          :min-prices="doctor.min_prices"
          :show-price-block="showPriceBlock"
          :loyalty-program="clinicLoyaltyProgram || undefined"
          @input="placeType = $event"
        />
        <DoctorCardReview
          v-if="positiveRate && !isMobile"
          :href="`${doctorPageUrl}#otzivi`"
          :text="positiveRate"
        />
      </div>

      <div
        v-if="showRightColumn"
        class="lpu-doctor-card__schedule d-flex flex-column flex-shrink-0 ga-6"
        :class="{ 'lpu-doctor-card__schedule--desktop': !isMobile }"
      >
        <div
          v-if="isMobile"
          class="lpu-doctor-card__divider"
        />
        <DoctorRestrictionsBanner
          v-if="doctor.has_restriction_appointment"
          :doctor-id="doctor.id"
          :lpu="lpu"
        />
        <DoctorCardAppointment
          :doctor="doctor"
          :lpu-phone-href="lpuPhoneHref"
          :lpu-not-working-text="lpuNotWorkingText"
          :home-call-label="homeCallLabel"
          :staff-appointment-type="staffAppointmentType"
          :place-type="placeType"
          :day-items="dayItems"
          :active-date="activeDate || undefined"
          :active-day-slots="activeDaySlots"
          :show-appointment-block="showAppointmentBlock"
          :show-calendar="showCalendar"
          :show-appointment-stub="showAppointmentStub"
          :show-home-call-button="showHomeCallButton"
          :show-clinic-call-button="showClinicCallButton"
          :is-card-slots-loading="isCardSlotsLoading"
          :is-express-appointment="isExpressAppointment"
          :is-express-closed="isExpressClosed"
          :is-staff="isStaff"
          @select-day="selectDay"
          @slot="onSlotClick"
        />
      </div>
    </div>
  </article>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import { format, parseISO } from 'date-fns'
import useIsMobile from 'src/composables/useIsMobile'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type {
  AppointmentPlaceType,
  LpuDoctor,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'
import type { DoctorSlotsByDate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/types'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import getDoctorPlaceTypes from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getDoctorPlaceTypes'
import getAppointmentRedirectType from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getAppointmentRedirectType'
import useDoctorCardSchedule from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/composables/useDoctorCardSchedule'
import useDoctorCardVisibility from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/composables/useDoctorCardVisibility'
import redirectToAppointment from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/functions/redirectToAppointment'
import DoctorCardHeader from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorCardHeader.vue'
import DoctorCardIdentity from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorCardIdentity.vue'
import DoctorCardActions from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorCardActions.vue'
import DoctorCardReview from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorCardReview.vue'
import DoctorCardAppointment from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorCardAppointment.vue'
import DoctorDocumentsPlate from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorDocumentsPlate.vue'
import DoctorRestrictionsBanner from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorRestrictionsBanner.vue'
import DoctorCardOffer from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/components/DoctorCardOffer.vue'

const props = defineProps<{
  doctor: LpuDoctor
  lpu: Lpu
  clinicSlots: DoctorSlotsByDate
  isSlotsLoading: boolean
  doctorPageUrl: string
  lpuNotWorkingText: string
}>()

const isMobile = useIsMobile()
const placeTypes = computed(() => getDoctorPlaceTypes(props.doctor.min_prices))
const placeType = ref<AppointmentPlaceType>(placeTypes.value[0] || 'clinic')
const positiveRate = computed(() => tDynamic(props.doctor, 'positive_rate'))

const {
  dayItems,
  activeDate,
  activeDaySlots,
  hasBookableSlots,
  isTelemedLoading,
  selectDay,
} = useDoctorCardSchedule({
  doctor: props.doctor,
  lpuId: props.lpu.id,
  clinicSlots: toRef(props, 'clinicSlots'),
  placeType,
})

const {
  isStaff,
  staffAppointmentType,
  isExpressAppointment,
  isExpressClosed,
  showAppointmentBlock,
  showPriceBlock,
  clinicLoyaltyProgram,
  lpuPhoneHref,
  showHomeCallButton,
  showClinicCallButton,
  homeCallLabel,
  isCardSlotsLoading,
  showCalendar,
  showAppointmentStub,
  showRightColumn,
} = useDoctorCardVisibility({
  doctor: props.doctor,
  lpu: props.lpu,
  isMobile,
  placeType,
  isSlotsLoading: toRef(props, 'isSlotsLoading'),
  hasBookableSlots,
  isTelemedLoading,
})

function onSlotClick(time: string) {
  const specialityId = props.doctor.workplace_info?.specialities?.[0]?.id

  if (!specialityId || !activeDate.value) {
    return
  }

  redirectToAppointment({
    doctorId: props.doctor.id,
    lpuId: props.lpu.id,
    slot: `${format(parseISO(activeDate.value), 'dd.MM.yyyy')} ${time}`,
    specialityId,
    type: getAppointmentRedirectType(placeType.value, props.doctor.min_prices),
  })
}
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.lpu-doctor-card {
  box-shadow: 0 2px 24px 0 rgba(9, 42, 72, 0.08),
    0 0 2px 0 rgba(21, 40, 92, 0.06),
    0 1px 2px 0 rgba(5, 23, 40, 0.08);

  &__actions {
    position: absolute;
    top: $border-radius-xl;
    right: $border-radius-xl;
    z-index: 1;
  }

  &__divider {
    height: 1px;
    background-color: $ui-kit-bg-gray-60;
  }

  &__schedule {
    min-width: 0;
    width: 100%;

    &--desktop {
      width: 396px;
    }
  }
}
</style>
